import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '../ui/badge.tsx';
import { DataTableColumnHeader } from '../data-table/data-table-column-header.tsx';
import { formatDate, formatFullTimestamp, formatRelativeTime } from '../../lib/date.ts';
import { CustomerCell } from './cells/CustomerCell.tsx';
import { RoleCell } from './cells/RoleCell.tsx';
import { TypeCell } from './cells/TypeCell.tsx';
import {
  type UserRowActionKind,
  UserRowActions,
  type UsersTablePermissions,
} from './UserRowActions.tsx';
import type { UsersTableRow, UsersTableScope } from './types.ts';

/** Default sort, applied on both mounts. */
export const USERS_TABLE_DEFAULT_SORTING = [{ id: 'name', desc: false }] as const;

export interface UsersTableColumnContext {
  can: UsersTablePermissions;
  onAction: (kind: UserRowActionKind, row: UsersTableRow) => void;
}

/**
 * The nine columns, in order: Name, Email, Type, Customer, Role, Status,
 * Created, Last Login, Actions.
 *
 * Name, Email, Created, Last Login and Actions are `UsersPage`'s column
 * definitions moved here as they were. Type, Customer and Role replace the one
 * overloaded Customer column. Two rules hold throughout:
 *
 * 1. **Cells read the row model, they never re-derive.** Every derived value
 *    comes from `toRow` (`derive.ts`) and `lib/user-status.ts`, so the columns
 *    on `/users` and the row set on a customer page cannot disagree about the
 *    same user. This is the only edit the moved cells needed: `row.type` and
 *    `row.propertyCount` in place of the payload's own `isMetronInternal` /
 *    `isPropertyUser` flags, and `row.status` in place of the page's local
 *    `getStatusBadge` / rank `accessorFn`.
 * 2. **Each sort accessor returns exactly the string its cell renders**
 *    (`row.sortKeys`). Status is the one documented exception: it sorts by
 *    numeric rank so the order stays Disabled → … → Verified (CONFIRMED), as it is today.
 *
 * `Type` and `Customer` are the columns that vary by mount: both are dropped
 * in `customer` mode, where the route already fixes the customer and a
 * customer's users are all external.
 */
export function buildColumns(
  scope: UsersTableScope,
  { can, onAction }: UsersTableColumnContext,
): ColumnDef<UsersTableRow>[] {
  const columns: ColumnDef<UsersTableRow>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Name' />,
      cell: ({ row }) => <div className='font-medium'>{row.original.name}</div>,
    },
    {
      accessorKey: 'email',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Email' />,
    },
  ];

  if (scope.mode === 'all') {
    columns.push(
      {
        id: 'type',
        accessorFn: (row) => row.sortKeys.type,
        header: ({ column }) => <DataTableColumnHeader column={column} title='Type' />,
        cell: ({ row }) => <TypeCell row={row.original} />,
      },
      {
        id: 'customer',
        accessorFn: (row) => row.sortKeys.customer,
        header: ({ column }) => <DataTableColumnHeader column={column} title='Customer' />,
        cell: ({ row }) => <CustomerCell row={row.original} />,
      },
    );
  }

  columns.push(
    {
      id: 'role',
      accessorFn: (row) => row.sortKeys.role,
      header: ({ column }) => <DataTableColumnHeader column={column} title='Role' />,
      cell: ({ row }) => <RoleCell row={row.original} />,
    },
    {
      id: 'status',
      accessorFn: (row) => row.sortKeys.status,
      header: ({ column }) => <DataTableColumnHeader column={column} title='Status' />,
      cell: ({ row }) => (
        <Badge variant={row.original.status.variant}>{row.original.status.label}</Badge>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Created' />,
      cell: ({ row }) => (
        <span className='text-xs text-muted-foreground'>
          {formatDate(row.getValue('createdAt'))}
        </span>
      ),
    },
    {
      id: 'lastLogin',
      // `undefined`, not `null`, so `sortUndefined` applies: never-logged-in
      // users sort last in both directions instead of landing wherever `null`
      // happens to compare.
      accessorFn: (row) => row.lastLoginAt ?? undefined,
      sortUndefined: 'last',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Last Login' />,
      cell: ({ row }) => {
        const lastLoginAt = row.original.lastLoginAt;
        if (!lastLoginAt) {
          return <span className='text-xs text-muted-foreground'>&mdash;</span>;
        }
        return (
          <span
            className='text-xs text-muted-foreground'
            title={formatFullTimestamp(lastLoginAt)}
          >
            {formatRelativeTime(lastLoginAt)}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: () => <div className='text-right'>Actions</div>,
      cell: ({ row }) => (
        <div className='text-right'>
          <UserRowActions row={row.original} can={can} onAction={onAction} />
        </div>
      ),
    },
  );

  return columns;
}
